import React, { FC, useContext, useState } from "react";
import { UserContext } from "features/account";
import { useVerificationAccess } from "features/credits";
import Button from "shared/ui/Button";
import { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { ReactComponent as IconBack } from "assets/img/icons/icon_arrow-Right.svg";
import { ReactComponent as IconSearch } from "assets/img/icons/icon_search2.svg";
import { ReactComponent as IconIDVerifyDoc } from "assets/img/icons/icon_id_verify_doc.svg";
import { ReactComponent as IconIDVerifyIdentity } from "assets/img/icons/icon_id_verify_identity.svg";
import { ReactComponent as IconDownload } from "assets/img/icons/icon_download2.svg";
import { ReactComponent as IconCoordonee } from "assets/img/icons/icon_coodonnee.svg";
import { ReactComponent as IconWarning } from "assets/img/icons/icon_Warning.svg";
import { ReactComponent as IconAnalysis } from "assets/img/icons/icon_analysis.svg";
import { ReactComponent as IconSignal } from "assets/img/icons/icon_signal.svg";
import VideoFrame from "shared/ui/VideoFrame";
import Checkbox from "shared/ui/Checkbox";
import { useNavigate } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import { AnimatePresence, motion } from "framer-motion";
import { CREDITS_PRICING, Pricing } from "shared/config/pricings";
import Modal from "shared/ui/Modal";
import FAQ from "components/FAQ";
import { faqDataIDVerify } from "components/FAQ/faqData"; // Import the new FAQ component

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

type VerifyOverViewProps = {
    showOverview: boolean;
    setShowOverview: React.Dispatch<React.SetStateAction<boolean>>;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    selectedNeverShowAgain: boolean;
    setSelectedNeverShowAgain: React.Dispatch<React.SetStateAction<boolean>>;
    handleStartVerification: any;
    setCheckAnotherOne: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedCheckbox: React.Dispatch<React.SetStateAction<boolean>>;
    selectedCheckbox: boolean;
};

const VerifyOverview: FC<VerifyOverViewProps> = ({
                                                     showModal,
                                                     setShowModal,
                                                     selectedNeverShowAgain,
                                                     setSelectedNeverShowAgain,
                                                     handleStartVerification,
                                                     setCheckAnotherOne,
                                                     setSelectedCheckbox,
                                                     selectedCheckbox,
                                                 }) => {
    const { user, userType } = useContext(UserContext);
    const credits = user?.credits ?? 0;
    // Accès = solde OU vérifications gratuites du mois (règle unique, features/credits).
    const { canUse } = useVerificationAccess();
    const canVerify = credits > 0 || canUse;
    const navigate = useNavigate();

    const handleStartVerify = () => {
        setShowModal(true);
    };

    const buyCreditsPage = () => {
        navigate(`${userType === "partner" ? "/professionnels" : "/particuliers"}/credits-pricing`)
    };

    const buyCredits = (index: number) => {
        navigate(`${userType === "partner" ? "/professionnels" : "/particuliers"}/payment/credits/${index}/onetime`)
    };

    const BtnValiderForDocument = ({ className }: any) => {
        return (
          <Button
            icon={<IconSearch className="PageHeader__icon" />}
            label={"Vérifier"}
            type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
            style={Button_Style.SOLID}
            disabled={!canVerify}
            onClick={handleStartVerify}
            className={`d-flex flex-row justify-content-center align-items-center flex-reverse ${className}`}
          />
        );
    };

    const BtnValiderForIdentity = ({ className }: any) => {
        return (
          <Button
            icon={<IconSearch className="PageHeader__icon" />}
            label={"Vérifier"}
            type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
            style={Button_Style.SOLID}
            disabled={!canVerify}
            onClick={() => {
                setCheckAnotherOne(true);
                handleStartVerification(false);
            }}
            className={`d-flex flex-row flex-reverse justify-content-center align-items-center ${className}`}
          />
        );
    };

    const BtnAcheterCredit = ({ className }: any) => {
        return (
          <Button
            label={"Acheter"}
            type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
            style={Button_Style.OUTLINED}
            className={`rounded-lg ${className} d-flex flex-row justify-content-center align-items-center`}
            onClick={buyCreditsPage}
          />
        );
    };

    return (
      <div className="text-justify d-flex flex-column gap-1 mb-8 mx-4 mt-2">
          <div className={`VerifyPage__row row ${!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) ? '' : 'd-flex'} justify-content-center`}>
              {/* Section Gauche */}
              {!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) && (
                <div className="col-sm-6">
                    <div className="card d-flex flex-column">
                        <div className="text-secondary">
                            <div>
                                <div className="d-flex gap-1 justify-content-between align-items-center flex-wrap">
                                    <div className="d-flex flex-column">
                                        <div className="text-primary text-mm bold">
                                            Contrôler un document d'identité
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Vérifier l'authenticité d'un document en quelques clics
                                        </div>
                                    </div>
                                    {!isMobile && <BtnValiderForDocument />}
                                </div>
                                <hr className="border-t-grey-10" />
                                <div className="bg-blue-light d-flex justify-content-center align-items-center rounded">
                                    <IconIDVerifyDoc
                                      className="PageHeader__icon pointer"
                                      width={100}
                                      onClick={() => {
                                          if (canVerify) {
                                              handleStartVerify();
                                          }
                                      }}
                                    />
                                </div>
                                <div className="text-primary d-flex flex-column gap-1 mt-2 text-sm">
                                    <div className="d-flex flex-row gap-05 align-items-center">
                                        <IconDownload className="PageHeader__icon" width={50} />
                                        <span>Téléchargez le document</span>
                                    </div>
                                    <div className="d-flex flex-row gap-05 align-items-center">
                                        <IconAnalysis className="PageHeader__icon" width={50} />
                                        <span>Analyse du document transmis</span>
                                    </div>
                                    <div className="d-flex flex-row gap-05 align-items-center">
                                        <IconWarning className="PageHeader__icon" width={50} />
                                        <span>Détection des incohérences</span>
                                    </div>
                                </div>
                                {isMobile && <BtnValiderForDocument className="w-100 mt-3" />}
                            </div>
                        </div>
                    </div>
                </div>
              )}

              <div className={`${!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) ? 'col-sm-6' : 'w-2/3'}`}>
                  <div className="card d-flex flex-column h-100">
                      <div className="text-secondary">
                          <div>
                              <div className="d-flex gap-1 justify-content-between align-items-center flex-wrap">
                                  <div className="d-flex flex-column">
                                      <div className="text-primary text-mm bold">
                                          Vérifier une identité
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                          Vérifier les coordonnées d'une personne
                                      </div>
                                  </div>
                                  {!isMobile && <BtnValiderForIdentity />}
                              </div>
                              <hr className="border-t-grey-10" />
                              <div className="bg-blue-light d-flex justify-content-center align-items-center rounded">
                                  <IconIDVerifyIdentity
                                    className="PageHeader__icon pointer"
                                    width={100}
                                    onClick={() => {
                                        if (canVerify) {
                                            setCheckAnotherOne(true);
                                            handleStartVerification(false);
                                        }
                                    }}
                                  />
                              </div>
                              <div className="text-primary d-flex flex-column gap-1 mt-2 text-sm">
                                  <div className="d-flex flex-row gap-05 align-items-center">
                                      <IconCoordonee className="PageHeader__icon" width={50} />
                                      <span>Transmettez les coordonnées : nom, prénom, mail, num de tel</span>
                                  </div>
                                  <div className="d-flex flex-row gap-05 align-items-center">
                                      <IconAnalysis className="PageHeader__icon" width={50} />
                                      <span>Analyse des données transmises</span>
                                  </div>
                                  <div className="d-flex flex-row gap-05 align-items-center">
                                      <IconSignal className="PageHeader__icon" width={50} />
                                      <span>Score de confiance affiché</span>
                                  </div>
                              </div>
                              {isMobile && <BtnValiderForIdentity className="w-100 mt-3" />}
                          </div>
                      </div>
                  </div>
              </div>
          </div>

          <div className={`VerifyPage__row row ${!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) ? '' : 'd-flex'} justify-content-center flex-wrap-reverse`}>
              {/* Section Gauche */}

              {!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) && (
                <div className="col-sm-6">
                    <div className="card d-flex flex-column h-100">
                        <div className="text-secondary">
                            <div>
                                <div className="d-flex gap-1 justify-content-between align-items-center flex-wrap">
                                    <div className="d-flex flex-row gap-05 justify-content-center align-items-center">
                                        <div
                                          className={`border p-2 px-3 bold text-md rounded border-neutral-50 ${credits < 3 ? "text-error" : "text-primary"}`}>
                                            {credits}
                                        </div>
                                        <div className="d-flex flex-column">
                                            <div className="text-primary text-mm bold">
                                                Solde de vérifications
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Acheter des vérifications supplémentaires
                                            </div>
                                        </div>
                                    </div>
                                    {!isMobile && <BtnAcheterCredit />}
                                </div>
                                <hr className="border-t-grey-10" />
                                <div className="text-primary d-flex flex-row flex-wrap gap-1 mt-2 text-sm w-100">
                                    {CREDITS_PRICING.map((credit, index) => (
                                      <div
                                        key={index}
                                        className="d-flex flex-column gap-05 border-2 pointer hover:shadow-xl rounded p-2 border-primary-70 w-30"
                                        onClick={() => buyCredits(index)}
                                      >
                                          <div className="d-flex flex-column">
                                              <span className="text-secondary bold text-xl">{credit.creditNumber}</span>
                                              <span className="text-muted-foreground text-xs">Vérifications</span>
                                          </div>
                                          <span className="text-primary text-2xl bold">{credit.price}€</span>
                                      </div>
                                    ))}
                                </div>
                                {isMobile && <BtnAcheterCredit className="w-100 mt-3 rounded" />}
                            </div>
                        </div>
                    </div>
                </div>
              )}

              {/* Section FAQ */}
            <div className={`${!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0) ? 'col-sm-6' : 'w-100'}`}>
              <FAQ
                initialDisplayCount={3}
                title="FAQ"
                showViewAll={true}
                faqData={faqDataIDVerify}
              />
            </div>
          </div>
      </div>
    );
};

export default VerifyOverview;